import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
} from 'react';
import { DOMAIN_CONFIGS } from '../../configs';
import {
  getDefaultCountry,
  getDefaultCurrency,
  getDefaultDirection,
  getDefaultLanguage,
  IDirection,
} from './utils';
import { getStorageKey, setCookie } from '../../utils';
import { IUserInfoData } from '../../hooks';
import { getDemandListStatistics } from './api';
import { IDemandListStatistics } from './interfaces';

export interface IAppConfigContext {
  /** 语言 */
  language: string;
  /** 修改语言 */
  updateLanguage: (val: string) => void;
  /** 国家/地区 */
  country: string;
  // /** 修改国家/地区 */
  updateCountry: (val: string) => void;
  /** 币种 */
  currency: string;
  /** 修改币种 */
  updateCurrency: (val: string) => void;
  /** 方向 */
  direction: IDirection;
  /** 用户信息 */
  userInfo: IUserInfoData | null | undefined;
  /** 修改用户信息 */
  updateUserInfo: (val: IUserInfoData | null | undefined) => void;
  demandListStatistics?: IDemandListStatistics;
  restDemandListStatistics: () => void;
}

export const AppConfigContext = createContext<IAppConfigContext>({} as IAppConfigContext);

export const useLanguage = () => {
  const ctx = useContext(AppConfigContext);
  return ctx.language;
};
export const useCountry = () => {
  const ctx = useContext(AppConfigContext);
  return ctx.country;
};
export const useCurrency = () => {
  const ctx = useContext(AppConfigContext);
  return ctx.currency;
};
export const useDirection = () => {
  const ctx = useContext(AppConfigContext);
  return ctx.direction;
};

export const useUserInfo = () => {
  const ctx = useContext(AppConfigContext);
  return ctx.userInfo;
};

const AppConfigProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState(getDefaultLanguage());
  const [country, setCountry] = useState(getDefaultCountry());
  const [currency, setCurrency] = useState(getDefaultCurrency());
  const [direction, setDirection] = useState<IDirection>(getDefaultDirection());
  const [demandListStatistics, setDemandListStatistics] = useState<IDemandListStatistics>();
  const [userInfo, setUserInfo] = useState<IUserInfoData | null | undefined>(null);

  /**
   * 修改语言
   * @param value 语言编码
   */
  const updateLanguage = useCallback((value: string) => {
    // localStorage.clear();
    setLanguage(value);
    localStorage.setItem(getStorageKey('language'), value);
    setCookie('languageCode', value);
    updateDirection(value === 'ar' ? 'rtl' : 'ltr');
  }, []);

  /**
   * 修改国家
   * @param value 国家编码
   */
  const updateCountry = useCallback((value: string) => {
    setCountry(value);
    setCookie('countryCode', value);
    localStorage.setItem(getStorageKey('country'), value);
  }, []);
  /**
   * 修改币种
   * @param value 币种编码
   */
  const updateCurrency = useCallback((value: string) => {
    setCurrency(value);
    localStorage.setItem(getStorageKey('currency'), value);
  }, []);
  /**
   * 修改方向
   * @param dir 方向
   */
  const updateDirection = (dir: IDirection) => {
    setDirection(dir);
    localStorage.setItem(getStorageKey('direction'), dir);
    document.dir = dir;
  };
  const updateUserInfo = useCallback((val: IUserInfoData | null | undefined) => {
    setUserInfo(val);
  }, []);

  /**
   * 初始化为sinomotive的域名配置
   */
  const initSinoMotiveOrigin = useCallback(() => {
    const lCountry = localStorage.getItem(getStorageKey('country'));
    const lCurrency = localStorage.getItem(getStorageKey('currency'));
    const sinomotiveDefaults = DOMAIN_CONFIGS.SINOMOTIVE.defaults;
    if (!lCountry) {
      updateCountry(sinomotiveDefaults.country);
      updateCurrency(sinomotiveDefaults.currency);
    }
    if (lCountry && !lCurrency) {
      updateCurrency(sinomotiveDefaults.currency);
    }
  }, [updateCountry, updateCurrency]);

  useEffect(() => {
    initSinoMotiveOrigin();

    const lLanguage = localStorage.getItem(getStorageKey('language'));
    const lDirection = localStorage.getItem(getStorageKey('direction'));
    if (!lDirection) {
      updateDirection(lLanguage === 'ar' ? 'rtl' : 'ltr');
    }
  }, []);

  useEffect(() => {
    const visibilitychange = () => {
      // 页面显示(从后台切换为显示时)，判断是否要更新数据
      if (document.visibilityState === 'visible') {
        const lLanguage = localStorage.getItem(getStorageKey('language')) || '';
        const lCurrency = localStorage.getItem(getStorageKey('currency')) || '';
        const lDirection = localStorage.getItem(getStorageKey('direction')) || 'ltr';

        let isReload = false;

        if (lCurrency !== currency) {
          setCurrency(lCurrency);
          isReload = true;
        }
        if (lLanguage !== language) {
          setLanguage(lLanguage);
          isReload = true;
        }
        if (lDirection !== direction) {
          setLanguage(lLanguage);
          isReload = true;
        }
        if (isReload) {
          const timer = setTimeout(() => {
            clearTimeout(timer);
            window.location.reload();
          }, 500);
        }
      }
    };
    document.addEventListener('visibilitychange', visibilitychange);
    return () => {
      document.removeEventListener('visibilitychange', visibilitychange);
    };
  }, [currency, language, direction]);

  useLayoutEffect(() => {
    document.dir = direction;
  }, []);

  const restDemandListStatistics = useCallback(async () => {
    try {
      const res = await getDemandListStatistics(country);
      if (res?.code === 200) {
        setDemandListStatistics(res.data);
      } else {
        setDemandListStatistics(undefined);
      }
    } catch (error) {
      setDemandListStatistics(undefined);
    }
  }, [country]);

  return (
    <AppConfigContext.Provider
      value={{
        language,
        updateLanguage,
        country,
        updateCountry,
        currency,
        updateCurrency,
        direction,
        userInfo: userInfo,
        updateUserInfo,
        demandListStatistics,
        restDemandListStatistics,
      }}
    >
      {children}
    </AppConfigContext.Provider>
  );
};

export default AppConfigProvider;
